import { useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Space, Table } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";

import { BLOG_LIMITS, isValidSlug, suggestSlug } from "@hedefuni/domain/blog";

import { api, ApiError } from "../api";
import type { BlogCategory } from "../types";

type Values = { name: string; slug: string; description: string; sortOrder: number };

/**
 * /blog/categories (permission `blog.manage`): topics posts are grouped by; each has its own
 * page (hedefuni.com/blog/kategori/...) with its description. Deleting one keeps its posts.
 */
export function BlogCategoriesPage() {
  const navigate = useNavigate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  const [editing, setEditing] = useState<BlogCategory | "new" | null>(null);
  const [saving, setSaving] = useState(false);
  const { data: categories, isLoading } = useQuery({
    queryKey: ["office", "blog", "categories"],
    queryFn: () => api<BlogCategory[]>("/api/office/blog/categories"),
  });

  function edit(category: BlogCategory | "new") {
    setEditing(category);
    if (category === "new") {
      form.resetFields();
      form.setFieldValue("sortOrder", 0);
    } else {
      form.setFieldsValue({
        name: category.name,
        slug: category.slug,
        description: category.description ?? "",
        sortOrder: category.sortOrder,
      });
    }
  }

  async function save() {
    const values = await form.validateFields();
    setSaving(true);
    const body = {
      ...values,
      description: values.description ?? "",
      sortOrder: values.sortOrder ?? 0,
    };
    try {
      if (editing === "new") await api("/api/office/blog/categories", { method: "POST", body });
      else if (editing) {
        await api(`/api/office/blog/categories/${editing.id}`, { method: "PUT", body });
      }
      await queryClient.invalidateQueries({ queryKey: ["office", "blog"] });
      setEditing(null);
    } catch (cause) {
      open?.({
        type: "error",
        message: cause instanceof ApiError ? cause.message : "Kaydedilemedi.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function remove(category: BlogCategory) {
    try {
      await api(`/api/office/blog/categories/${category.id}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: ["office", "blog"] });
    } catch (cause) {
      open?.({ type: "error", message: cause instanceof ApiError ? cause.message : "Silinemedi." });
    }
  }

  return (
    <Card
      title="Blog kategorileri"
      extra={
        <Space>
          <Button onClick={() => navigate("/blog")}>Yazılara dön</Button>
          <Button type="primary" onClick={() => edit("new")}>
            Yeni kategori
          </Button>
        </Space>
      }
    >
      <Table<BlogCategory>
        rowKey="id"
        loading={isLoading}
        dataSource={categories}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "Henüz kategori yok." }}
        columns={[
          { title: "Kategori", dataIndex: "name" },
          { title: "Adres", render: (_, category) => `/blog/kategori/${category.slug}` },
          { title: "Sıra", dataIndex: "sortOrder" },
          { title: "Yazı", dataIndex: "postCount" },
          {
            title: "",
            render: (_, category) => (
              <Space>
                <Button size="small" onClick={() => edit(category)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Kategori silinsin mi?"
                  description="Yazıları kategorisiz kalır."
                  okText="Sil"
                  cancelText="Vazgeç"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => remove(category)}
                >
                  <Button size="small" danger>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={editing === "new" ? "Yeni kategori" : "Kategoriyi düzenle"}
        open={editing !== null}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed: Partial<Values>) => {
            if (editing === "new" && "name" in changed) {
              form.setFieldValue("slug", suggestSlug(changed.name ?? ""));
            }
          }}
        >
          <Form.Item name="name" label="Ad" rules={[{ required: true, message: "Adı yaz." }]}>
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Adres (slug)"
            rules={[
              { required: true, message: "Adresi yaz." },
              {
                validator: (_, value: string) =>
                  !value || isValidSlug(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Sadece küçük harf, rakam ve tek tire.")),
              },
            ]}
          >
            <Input addonBefore="/blog/kategori/" maxLength={80} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Açıklama"
            extra="Kategori sayfasında gösterilir ve o sayfanın meta açıklaması olur."
          >
            <Input.TextArea
              maxLength={BLOG_LIMITS.descriptionMax}
              showCount
              autoSize={{ minRows: 2 }}
            />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sıra" extra="Küçük sayı önce gösterilir.">
            <InputNumber min={0} max={1000} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
